// ─────────────────────────────────────────────
//  MessagingPanel — the chat surface shell (Phase 5 messaging / UI-SPEC
//  "Panel open/close" + "Responsive Behavior"). A right-side drawer on desktop
//  (FLAG-1), full-screen on mobile, that satisfies the Modal/dialog a11y
//  contract regardless of chrome:
//
//    • role="dialog", aria-label="Messages"
//    • focus trapped (Tab / Shift+Tab cycle inside the panel)
//    • Escape closes; an aria-hidden backdrop closes on outside click
//    • focus restored to the trigger on close
//
//  Layout: ChannelList (master) + ConversationView (detail) side-by-side at
//  ≥640px; full-screen master/detail SWAP below 640px (list → tap → conversation
//  → "← Messages" back). The header carries the "Messages" title (22/700), a
//  close "✕", and a `headerSlot` where Plan 05 injects the ChimeMuteToggle.
//
//  Mounting from the app-shell topbar is Plan 05 — this component is the panel
//  internals, wired to useMessaging() via its children.
// ─────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import ChannelList from "./ChannelList";
import ConversationView from "./ConversationView";
import tokens from "../theme/tokens";

const { color, font, space, shadow } = tokens;

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

export default function MessagingPanel({ isOpen, onClose, headerSlot }) {
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const isMobile = useIsMobile(640);

  // Selection: { type:"channel", id } | { type:"dm", id, contactId, name } | null
  const [selection, setSelection] = useState(null);

  // Focus trap + Escape + focus restore (mirrors Modal's behavior).
  useEffect(() => {
    if (!isOpen) return undefined;
    triggerRef.current = document.activeElement;

    const panel = panelRef.current;
    const focusables = panel ? panel.querySelectorAll(FOCUSABLE) : [];
    if (focusables.length) focusables[0].focus();
    else if (panel) panel.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const items = panel ? panel.querySelectorAll(FOCUSABLE) : [];
        if (!items.length) {
          e.preventDefault();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      const trigger = triggerRef.current;
      if (trigger && typeof trigger.focus === "function") trigger.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // On mobile we swap master/detail: show the list until a row is picked, then
  // the conversation with a "← Messages" back affordance. On desktop both panes
  // are visible side-by-side.
  const showList = !isMobile || !selection;
  const showConversation = !isMobile || !!selection;

  return (
    <>
      {/* Backdrop is a SIBLING of the dialog (not an ancestor) so its
          aria-hidden never removes the dialog from the a11y tree. Outside
          click closes the panel (BoardAlertControls pattern). */}
      <div style={styles.backdrop} onMouseDown={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Messages"
        tabIndex={-1}
        style={{
          ...styles.panel,
          ...(isMobile ? styles.panelMobile : styles.panelDesktop),
        }}
      >
        <div style={styles.header}>
          <h2 style={styles.title}>Messages</h2>
          <div style={styles.headerRight}>
            {headerSlot}
            <button
              type="button"
              aria-label="Close messages"
              onClick={onClose}
              style={styles.close}
            >
              ✕
            </button>
          </div>
        </div>

        <div style={styles.layout}>
          {showList && (
            <div style={{ ...styles.master, ...(isMobile ? styles.masterMobile : {}) }}>
              <ChannelList selectedId={selection?.id} onSelect={setSelection} />
            </div>
          )}
          {showConversation && (
            <div style={styles.detail}>
              <ConversationView
                selection={selection}
                onBack={isMobile ? () => setSelection(null) : undefined}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.40)",
    zIndex: 1100,
  },
  panel: {
    position: "fixed",
    top: 0,
    right: 0,
    display: "flex",
    flexDirection: "column",
    background: color.surface.card,
    boxShadow: shadow.lg,
    fontFamily: font.family,
    boxSizing: "border-box",
    overflow: "hidden",
    zIndex: 1101, // above the backdrop
  },
  panelDesktop: {
    width: 480,
    maxWidth: "100vw",
    height: "100vh",
  },
  panelMobile: {
    width: "100vw",
    height: "100vh",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${space.md}px ${space.lg}px`,
    borderBottom: `1px solid ${color.border.default}`,
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: font.size.display, // 22
    fontWeight: font.weight.bold, // 700
    color: color.text.primary,
    lineHeight: font.lineHeight.tight,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
  },
  close: {
    minWidth: 44,
    minHeight: 44,
    background: "transparent",
    border: "none",
    color: color.text.secondary,
    cursor: "pointer",
    fontSize: font.size.heading,
    lineHeight: 1,
  },
  layout: {
    flex: 1,
    display: "flex",
    minHeight: 0,
  },
  master: {
    width: 240,
    flexShrink: 0,
    borderRight: `1px solid ${color.border.default}`,
    minHeight: 0,
  },
  masterMobile: {
    width: "100%",
    borderRight: "none",
  },
  detail: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
};
